import React, { useState, useEffect, useContext, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd } from "../../constants/styles";
import Ic from "../common/Ic";

// A renter's own sent inquiries — which properties they've messaged and where
// each one stands. Status labels mirror the landlord-side lead lifecycle but
// are phrased from the tenant's point of view (e.g. "new" → "Sent").
const STATUS = (h, t) => ({
  new:       { label: t.inqSent || "Sent",            color: h.ac },
  contacted: { label: t.inqContacted || "Contacted",  color: h.wn },
  visited:   { label: t.inqVisited || "Visited",       color: h.pp || h.ac },
  converted: { label: t.inqAccepted || "Accepted",     color: h.gn },
  rejected:  { label: t.inqClosed || "Closed",         color: h.rd },
});

export default function TenInquiriesPage() {
  const { t, h, go, setSelPropId, flash, showConfirm } = useContext(Ctx);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);

  useEffect(() => {
    let alive = true;
    api.getMyInquiries().then(d => {
      if (!alive) return;
      if (d && d.success) setInquiries(d.inquiries || []);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const statusCfg = STATUS(h, t);
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso || ""; }
  };

  // Long-press (touch hold, or mouse hold on desktop) enters multi-select mode.
  // A move or early release cancels it; the follow-up click is swallowed so the
  // pressed card doesn't immediately toggle back off or open its listing.
  const startPress = (id) => {
    longPressed.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      setSelectionMode(true);
      setSelected(prev => { const n = new Set(prev); n.add(id); return n; });
    }, 500);
  };
  const cancelPress = () => { clearTimeout(pressTimer.current); };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const exitSelection = () => { setSelectionMode(false); setSelected(new Set()); };

  const handleCardClick = (q) => {
    if (longPressed.current) { longPressed.current = false; return; } // swallow the click right after a long-press
    if (selectionMode) { toggleSelect(q.id); return; }
    if (q.property_id) { setSelPropId(q.property_id); go("mapView"); }
  };

  const deleteSelected = () => {
    if (selected.size === 0 || deleting) return;
    const ids = [...selected];
    const msg = ids.length === 1
      ? (t.removeOneInquiry || "Remove this inquiry from your list? The landlord still keeps the message you sent.")
      : (t.removeManyInquiries || "Remove these {n} inquiries from your list? The landlord still keeps the messages you sent.").replace("{n}", ids.length);
    showConfirm(msg, () => {
      setDeleting(true);
      api.hideMyInquiries(ids).then(r => {
        setDeleting(false);
        if (r && r.success) {
          setInquiries(prev => prev.filter(q => !selected.has(q.id)));
          exitSelection();
          flash(t.removedFromList || "Removed from your list");
        } else {
          flash((r && r.error) || t.couldntRemove || "Couldn't remove — please try again", "error");
        }
      }).catch(() => { setDeleting(false); flash(t.couldntRemove || "Couldn't remove — please try again", "error"); });
    });
  };

  // Withdraw = retract entirely: hard-deletes the lead so the landlord loses it
  // too (vs Remove, which only hides it from the tenant's own list).
  const withdrawSelected = () => {
    if (selected.size === 0 || deleting) return;
    const ids = [...selected];
    const msg = ids.length === 1
      ? (t.withdrawOneInquiry || "Withdraw this inquiry? It's permanently deleted and the landlord will no longer see it.")
      : (t.withdrawManyInquiries || "Withdraw these {n} inquiries? They're permanently deleted and the landlord will no longer see them.").replace("{n}", ids.length);
    showConfirm(msg, () => {
      setDeleting(true);
      api.withdrawMyInquiries(ids).then(r => {
        setDeleting(false);
        if (r && r.success) {
          setInquiries(prev => prev.filter(q => !selected.has(q.id)));
          exitSelection();
          flash(t.inquiryWithdrawn || "Inquiry withdrawn");
        } else {
          flash((r && r.error) || t.couldntWithdraw || "Couldn't withdraw — please try again", "error");
        }
      }).catch(() => { setDeleting(false); flash(t.couldntWithdraw || "Couldn't withdraw — please try again", "error"); });
    });
  };

  return (
    <div style={{ paddingTop: 16 }}>
      <button onClick={() => (selectionMode ? exitSelection() : go("dash"))} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← {t.back || "Back"}</button>

      {selectionMode ? (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8, background: h.cd, padding: "8px 8px 8px 14px", borderRadius: 12, border: `1px solid ${h.bd}` }}>
            <span style={{ color: h.tx, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{selected.size} {t.selected || "selected"}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={exitSelection} style={{ padding: "7px 11px", borderRadius: 8, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel || "Cancel"}</button>
              <button onClick={deleteSelected} disabled={selected.size === 0 || deleting} style={{ padding: "7px 11px", borderRadius: 8, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, cursor: selected.size === 0 || deleting ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: selected.size === 0 || deleting ? 0.5 : 1 }}>{t.removeFromMyList || "Remove"}</button>
              <button onClick={withdrawSelected} disabled={selected.size === 0 || deleting} style={{ padding: "7px 11px", borderRadius: 8, border: "none", background: h.rd, color: "#fff", cursor: selected.size === 0 || deleting ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: selected.size === 0 || deleting ? 0.5 : 1 }}>{t.withdraw || "Withdraw"}</button>
            </div>
          </div>
          <div style={{ color: h.ts, fontSize: 11, marginBottom: 16, lineHeight: 1.5, padding: "0 2px" }}>
            <b>{t.removeFromMyList || "Remove"}</b> {t.removeHintShort || "hides it from your list."} <b>{t.withdraw || "Withdraw"}</b> {t.withdrawHintShort || "deletes it — the landlord won't see it either."}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 20 }}>{"📩"}</span>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.myInquiries || "My Inquiries"}</h2>
            {!loading && inquiries.length > 0 && <span style={{ color: h.ac, fontSize: 13, fontWeight: 700 }}>({inquiries.length})</span>}
          </div>
          <div style={{ color: h.ts, fontSize: 12, marginBottom: 16 }}>{inquiries.length > 0 ? (t.myInquiriesHint || "Tip: press & hold a card to select and remove.") : (t.myInquiriesSub || "Properties you've sent an inquiry to")}</div>
        </>
      )}

      {loading ? (
        <div style={{ color: h.sc, fontSize: 13, textAlign: "center", padding: 32 }}>{(t.loading || "Loading") + "…"}</div>
      ) : inquiries.length === 0 ? (
        <div style={{ ...crd(h), textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{"📭"}</div>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{t.noInquiriesYet || "No inquiries yet"}</div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 6 }}>{t.noInquiriesDesc || "Browse properties and tap Send Inquiry to reach a landlord."}</div>
          <button onClick={() => go("mapView")} style={{ marginTop: 16, padding: "9px 20px", borderRadius: 10, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{"🗺️ "}{t.browseProperties || "Browse Properties"}</button>
        </div>
      ) : (
        inquiries.map((q) => {
          const cfg = statusCfg[q.status] || statusCfg.new;
          const isSel = selected.has(q.id);
          return (
            <div key={q.id}
              onClick={() => handleCardClick(q)}
              onTouchStart={() => startPress(q.id)}
              onTouchEnd={cancelPress}
              onTouchMove={cancelPress}
              onMouseDown={() => startPress(q.id)}
              onMouseUp={cancelPress}
              onMouseLeave={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
              style={{ ...crd(h), marginBottom: 12, cursor: (selectionMode || q.property_id) ? "pointer" : "default", userSelect: "none", WebkitUserSelect: "none", ...(isSel ? { border: `2px solid ${h.ac}`, background: `${h.ac}0d` } : {}) }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  {selectionMode && <span style={{ width: 20, height: 20, borderRadius: 999, border: `2px solid ${isSel ? h.ac : h.bd}`, background: isSel ? h.ac : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{isSel && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{"✓"}</span>}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: h.tx, fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                      <Ic n="bldg" s={14} c={h.ac} />{q.property_addr || (t.generalInquiry || "General Inquiry")}
                    </div>
                    {q.org_name && <div style={{ color: h.ts, fontSize: 11, marginTop: 2 }}>{(t.listedBy || "Listed by") + " " + q.org_name}</div>}
                  </div>
                </div>
                <span style={{ background: `${cfg.color}18`, color: cfg.color, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{cfg.label}</span>
              </div>
              {q.property_rent ? <div style={{ color: h.ts, fontSize: 11, marginTop: 4 }}>{"₹" + Number(q.property_rent).toLocaleString() + "/mo"}</div> : null}
              {q.message && <div style={{ color: h.ts, fontSize: 12, marginTop: 6, fontStyle: "italic", lineHeight: 1.4 }}>{"“" + q.message + "”"}</div>}
              {q.visit_status === "scheduled" && q.visit_date && <div style={{ color: h.pp || h.ac, fontSize: 11, marginTop: 6 }}>{"📅 " + (t.visitScheduled || "Visit scheduled") + ": " + fmtDate(q.visit_date)}</div>}
              <div style={{ color: h.tt || h.sc, fontSize: 10, marginTop: 6 }}>{(t.sentOn || "Sent") + " " + fmtDate(q.created_at)}</div>
              {!selectionMode && q.property_id && <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginTop: 8, display: "flex", alignItems: "center", gap: 3 }}>{t.viewDetails || "View Details"}<span style={{ opacity: 0.7 }}>{"›"}</span></div>}
            </div>
          );
        })
      )}
    </div>
  );
}
